import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { API } from "@/lib/constants";

const WHATSAPP_NUMBER = "+61491037005";
const WHATSAPP_LINK_BASE = "https://wa.me/61491037005";

const LABELS = {
  en: {
    tooltip: "Need help?",
    title: "Get help",
    sub: "Stuck? We usually reply within 24h.",
    name: "Name",
    email: "Email",
    subject: "Subject",
    message: "Your message",
    subject_ph: "e.g. Can't upload my CV",
    message_ph: "Describe what's happening. Include screenshots or your Seek URL if it helps.",
    send: "Send message",
    sending: "Sending…",
    or: "or",
    whatsapp: "Chat on WhatsApp",
    whatsapp_prefill: "Hey AussieCV team, I need help with:",
    success_title: "Message sent",
    success_sub: "Thanks! We'll reply to your email as soon as possible.",
    close: "Close",
    required: "Please write at least a short message.",
  },
  fr: {
    tooltip: "Besoin d'aide ?",
    title: "Nous contacter",
    sub: "Bloqué ? On répond en général en moins de 24h.",
    name: "Nom",
    email: "Email",
    subject: "Objet",
    message: "Ton message",
    subject_ph: "ex : Je n'arrive pas à uploader mon CV",
    message_ph: "Décris ton problème. Ajoute des captures ou ton URL Seek si utile.",
    send: "Envoyer",
    sending: "Envoi…",
    or: "ou",
    whatsapp: "Nous écrire sur WhatsApp",
    whatsapp_prefill: "Salut l'équipe AussieCV, j'ai besoin d'aide avec :",
    success_title: "Message envoyé",
    success_sub: "Merci ! On te répond par email dès que possible.",
    close: "Fermer",
    required: "Merci d'écrire un petit message.",
  },
  de: {
    tooltip: "Hilfe?",
    title: "Hilfe erhalten",
    sub: "Steckst du fest? Wir antworten meist innerhalb von 24h.",
    name: "Name",
    email: "E-Mail",
    subject: "Betreff",
    message: "Deine Nachricht",
    subject_ph: "z.B. CV lässt sich nicht hochladen",
    message_ph: "Beschreibe dein Problem.",
    send: "Senden",
    sending: "Sende…",
    or: "oder",
    whatsapp: "Auf WhatsApp schreiben",
    whatsapp_prefill: "Hi AussieCV Team, ich brauche Hilfe mit:",
    success_title: "Nachricht gesendet",
    success_sub: "Danke! Wir antworten dir per E-Mail so schnell wie möglich.",
    close: "Schließen",
    required: "Bitte schreib eine kurze Nachricht.",
  },
  it: {
    tooltip: "Aiuto?",
    title: "Contattaci",
    sub: "Bloccato? Rispondiamo entro 24h.",
    name: "Nome",
    email: "Email",
    subject: "Oggetto",
    message: "Il tuo messaggio",
    subject_ph: "es. Non riesco a caricare il CV",
    message_ph: "Descrivi il problema.",
    send: "Invia",
    sending: "Invio…",
    or: "o",
    whatsapp: "Scrivici su WhatsApp",
    whatsapp_prefill: "Ciao team AussieCV, ho bisogno di aiuto con:",
    success_title: "Messaggio inviato",
    success_sub: "Grazie! Ti risponderemo per email al più presto.",
    close: "Chiudi",
    required: "Scrivi un breve messaggio.",
  },
  es: {
    tooltip: "¿Ayuda?",
    title: "Contáctanos",
    sub: "¿Atascado? Respondemos en menos de 24h.",
    name: "Nombre",
    email: "Correo",
    subject: "Asunto",
    message: "Tu mensaje",
    subject_ph: "ej. No puedo subir mi CV",
    message_ph: "Describe el problema.",
    send: "Enviar",
    sending: "Enviando…",
    or: "o",
    whatsapp: "Escríbenos por WhatsApp",
    whatsapp_prefill: "Hola equipo AussieCV, necesito ayuda con:",
    success_title: "Mensaje enviado",
    success_sub: "¡Gracias! Te responderemos por correo lo antes posible.",
    close: "Cerrar",
    required: "Escribe un mensaje breve.",
  },
  pt: {
    tooltip: "Ajuda?",
    title: "Fala connosco",
    sub: "Preso? Respondemos em menos de 24h.",
    name: "Nome",
    email: "Email",
    subject: "Assunto",
    message: "A tua mensagem",
    subject_ph: "ex. Não consigo carregar o CV",
    message_ph: "Descreve o problema.",
    send: "Enviar",
    sending: "A enviar…",
    or: "ou",
    whatsapp: "Fala no WhatsApp",
    whatsapp_prefill: "Olá equipa AussieCV, preciso de ajuda com:",
    success_title: "Mensagem enviada",
    success_sub: "Obrigado! Vamos responder por email o mais rápido possível.",
    close: "Fechar",
    required: "Escreve uma mensagem curta.",
  },
};

export function SupportWidget({ lang = "en", user }) {
  const t = LABELS[lang] || LABELS.en;
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    subject: "",
    message: "",
  });

  // Update prefill when user auth state changes
  React.useEffect(() => {
    setForm((f) => ({
      ...f,
      name: f.name || user?.name || "",
      email: f.email || user?.email || "",
    }));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if ((form.message || "").trim().length < 5) {
      setError(t.required);
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/support/contact`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, lang }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const closeAndReset = () => {
    setOpen(false);
    setTimeout(() => {
      setDone(false);
      setError("");
      setForm({
        name: user?.name || "",
        email: user?.email || "",
        subject: "",
        message: "",
      });
    }, 300);
  };

  const waLink = () => {
    const msg = form.subject
      ? `${t.whatsapp_prefill} ${form.subject}`
      : t.whatsapp_prefill;
    return `${WHATSAPP_LINK_BASE}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <>
      {/* Floating button */}
      <motion.button
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        transition={{ delay: 0.6, type: "spring", stiffness: 300 }}
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-[#292524] text-white shadow-2xl hover:bg-[#1c1917] flex items-center justify-center group"
        aria-label={t.tooltip}
        data-testid="support-widget-btn"
        title={t.tooltip}
      >
        <MessageCircle className="w-6 h-6" />
        <span className="absolute top-0 right-0 w-3 h-3 bg-[#3B8268] rounded-full ring-2 ring-white animate-pulse" />
      </motion.button>

      {/* Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeAndReset}
            data-testid="support-modal"
          >
            <motion.div
              className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl shadow-2xl overflow-hidden"
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 26 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-[#292524] text-white px-6 py-5 flex items-start justify-between gap-3">
                <div>
                  <div className="font-heading font-extrabold text-xl">{t.title}</div>
                  <div className="text-xs text-white/70 mt-0.5">{t.sub}</div>
                </div>
                <button onClick={closeAndReset} className="text-white/70 hover:text-white transition-colors" data-testid="support-close">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6">
                {done ? (
                  <div className="text-center py-6" data-testid="support-success">
                    <div className="w-14 h-14 rounded-2xl bg-[#3B8268]/10 mx-auto mb-4 flex items-center justify-center">
                      <CheckCircle2 className="w-7 h-7 text-[#3B8268]" />
                    </div>
                    <div className="font-heading font-bold text-lg text-[#292524] mb-2">{t.success_title}</div>
                    <p className="text-sm text-[#57534E] mb-5">{t.success_sub}</p>
                    <button onClick={closeAndReset} className="btn-primary w-full">{t.close}</button>
                  </div>
                ) : (
                  <>
                    <form onSubmit={submit} className="space-y-3">
                      {!user && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[11px] font-semibold text-[#57534E] mb-1">{t.name}</label>
                            <input
                              className="input !py-2 !text-sm"
                              value={form.name}
                              onChange={(e) => setForm({ ...form, name: e.target.value })}
                              data-testid="support-name"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold text-[#57534E] mb-1">{t.email}</label>
                            <input
                              type="email"
                              className="input !py-2 !text-sm"
                              value={form.email}
                              onChange={(e) => setForm({ ...form, email: e.target.value })}
                              data-testid="support-email"
                            />
                          </div>
                        </div>
                      )}
                      <div>
                        <label className="block text-[11px] font-semibold text-[#57534E] mb-1">{t.subject}</label>
                        <input
                          className="input !py-2 !text-sm"
                          placeholder={t.subject_ph}
                          value={form.subject}
                          onChange={(e) => setForm({ ...form, subject: e.target.value })}
                          data-testid="support-subject"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-[#57534E] mb-1">{t.message}</label>
                        <textarea
                          rows={4}
                          className="input !text-sm !py-2 resize-none"
                          placeholder={t.message_ph}
                          value={form.message}
                          onChange={(e) => setForm({ ...form, message: e.target.value })}
                          required
                          data-testid="support-message"
                        />
                      </div>
                      {error && (
                        <div className="text-xs text-[#D9423E] flex items-center gap-1.5">
                          <AlertTriangle className="w-3.5 h-3.5" /> {error}
                        </div>
                      )}
                      <button
                        type="submit"
                        className="btn-primary w-full"
                        disabled={sending}
                        data-testid="support-submit"
                      >
                        {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> {t.sending}</> : <><Send className="w-4 h-4" /> {t.send}</>}
                      </button>
                    </form>

                    {/* Divider */}
                    <div className="flex items-center gap-3 my-4">
                      <div className="flex-1 h-px bg-[#EAE5DB]" />
                      <span className="text-xs text-[#78716C] uppercase font-semibold">{t.or}</span>
                      <div className="flex-1 h-px bg-[#EAE5DB]" />
                    </div>

                    {/* WhatsApp CTA */}
                    <a
                      href={waLink()}
                      target="_blank"
                      rel="noreferrer"
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#25D366] hover:bg-[#20BC5A] text-white font-bold text-sm transition-colors"
                      data-testid="support-whatsapp"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      {t.whatsapp}
                    </a>
                    <div className="text-center text-[10px] text-[#78716C] mt-2 font-mono">{WHATSAPP_NUMBER}</div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
